import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from "@tanstack/react-query";
import { Request } from "./requests";
import {
  TExperience,
  ForumPost,
  SingleForumPostResponse,
  GetCommentsResponse,
  CommentWithFullAuthor,
  TCreateExperiencePayload,
  CreateReplyBody,
  CreateReplyResponse,
} from "./types";
import { AxiosProgressEvent } from "axios";

export type { TCreateExperiencePayload, ForumPost } from "./types";

type OnProgressUpdateHandler = (overallProgress: number) => void;

interface CreateExperienceVariables {
  payload: TCreateExperiencePayload;
  files: File[];
  onProgressUpdate?: OnProgressUpdateHandler;
}

/** Normalizes tags that may be string ("a,b") or string[] into a clean string[] */
const normalizeTags = (tags: unknown): string[] => {
  if (Array.isArray(tags)) {
    return tags
      .map(String)
      .map((t) => t.trim())
      .filter(Boolean);
  }
  if (typeof tags === "string") {
    return tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
};

export const useCreateExperience = (): UseMutationResult<
  TExperience,
  Error,
  CreateExperienceVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<TExperience, Error, CreateExperienceVariables>({
    mutationFn: async ({ payload, files, onProgressUpdate }) => {
      const form = new FormData();

      // Required fields
      form.append("title", payload.title);
      form.append("content", payload.content);

      // Tags: append as repeated "tags" keys (no brackets)
      const tagsArray = normalizeTags((payload as any).tags);
      tagsArray.forEach((tag) => form.append("tags", tag));

      // Files: append as repeated "media" keys (no brackets)
      if (files?.length) {
        for (const file of files) {
          form.append("media", file); // <- IMPORTANT: no []
        }
      }

      // Optional: progress init
      if (onProgressUpdate) onProgressUpdate(0);

      // (Optional) quick sanity log while debugging:
      // for (const [k, v] of form.entries()) {
      //   console.log("FD:", k, v instanceof File ? v.name : v);
      // }

      try {
        const res = await Request.post("/forum/posts", form, {
          onUploadProgress: (evt: AxiosProgressEvent) => {
            if (!onProgressUpdate || !evt.total) return;
            const pct = Math.round((evt.loaded * 100) / evt.total);
            onProgressUpdate(pct < 100 ? pct : 99); // keep 99% until server responds
          },
        });

        if (onProgressUpdate) onProgressUpdate(100);

        const data = res?.data?.data;
        if (!data) {
          throw new Error(
            res?.data?.message ||
              "Failed to create post: Invalid response structure from server."
          );
        }
        return data as TExperience;
      } catch (err: any) {
        if (onProgressUpdate) onProgressUpdate(0);
        const backendMessage = Array.isArray(err?.response?.data?.message)
          ? err.response.data.message.join(", ")
          : err?.response?.data?.message;
        throw new Error(
          backendMessage || err?.message || "Failed to create post."
        );
      }
    },

    onSuccess: () => {
      // Invalidate lists that show posts/experiences
      queryClient.invalidateQueries({ queryKey: ["forumPosts"] });
      queryClient.invalidateQueries({ queryKey: ["userExperiences"] });
    },
    onError: () => {
      // You can toast here if desired
    },
  });
};

const fetchAllForumPosts = async (sortBy?: string): Promise<ForumPost[]> => {
  const apiParams: { sortOrder?: string } = {};
  if (sortBy) {
    apiParams.sortOrder = sortBy;
  }

  try {
    const response = await Request.get("/forum/posts", { params: apiParams });

    if (
      response.data &&
      response.data.data &&
      Array.isArray(response.data.data.posts)
    ) {
      return response.data.data.posts as ForumPost[];
    }
    if (response.data && Array.isArray(response.data.posts)) {
      return response.data.posts as ForumPost[];
    }
    throw new Error(
      "Failed to fetch forum posts or data format is incorrect. Check API response."
    );
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(
      error.message || "An unknown error occurred while fetching posts."
    );
  }
};

export const useGetForumPosts = (
  sortBy?: "latest" | "popular" | string
): UseQueryResult<ForumPost[], Error> => {
  return useQuery<
    ForumPost[],
    Error,
    ForumPost[],
    (string | { sortBy?: string | undefined } | undefined)[]
  >({
    queryKey: ["forumPosts", { sortBy }],
    queryFn: () => fetchAllForumPosts(sortBy),
  });
};

export const useGetForumPostById = (
  postId: string | null | undefined
): UseQueryResult<ForumPost, Error> => {
  return useQuery<
    SingleForumPostResponse,
    Error,
    ForumPost,
    (string | string | null | undefined)[]
  >({
    queryKey: ["forumPost", postId],
    queryFn: async () => {
      if (!postId) throw new Error("Post ID is required");
      const response = await Request.get<SingleForumPostResponse>(
        `/forum/posts/${postId}`
      );
      return response.data;
    },
    select: (data) => {
      if (data && data.data) {
        return data.data;
      }
      throw new Error("Invalid data structure received for single post.");
    },
    enabled: !!postId,
  });
};

interface SummarizeTextVariables {
  textToSummarize: string;
}

interface SummarizeTextResponse {
  summary: string;
}

export const useSummarizeTextWithGemini = (): UseMutationResult<
  SummarizeTextResponse,
  Error,
  SummarizeTextVariables
> => {
  return useMutation<SummarizeTextResponse, Error, SummarizeTextVariables>({
    mutationFn: async ({ textToSummarize }) => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      if (textToSummarize.trim().split(/\s+/).length < 7) {
        return { summary: "" };
      }
      const words = textToSummarize.split(" ");
      const summary =
        words.slice(0, Math.min(words.length, 10)).join(" ") +
        (words.length > 10 ? "..." : "");
      return {
        summary: `${summary.charAt(0).toUpperCase() + summary.slice(1)}`,
      };
    },
    onSuccess: (data) => {},
    onError: (error: Error) => {},
  });
};

// Build multipart/form-data that matches FilesInterceptor('media', 10)
const toFormData = (body: CreateReplyBody) => {
  const fd = new FormData();
  fd.append("content", body.content);
  if (body.parentReply) fd.append("parentReply", body.parentReply);
  if (body.mediaFiles?.length) {
    body.mediaFiles.forEach((file) => fd.append("media", file));
  }
  return fd;
};

/**
 * Bind postId at the hook level so it never enters the request body.
 * This avoids "property postId should not exist" from ValidationPipe.
 */
export const useCreateReply = (postId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateReplyBody) => {
      const res = await Request.post<CreateReplyResponse>(
        `/forum/replies/post/${postId}`,
        toFormData(body),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const payload = res.data;
      if (
        !payload ||
        (payload.statusCode !== 200 && payload.statusCode !== 201)
      ) {
        throw new Error(payload?.message || "Failed to create reply.");
      }
      return payload;
    },
    onSuccess: () => {
      // Refresh the comments for this post (your existing query)
      queryClient.invalidateQueries({ queryKey: ["postComments", postId] });
      // (Optional) refresh post detail cache if you keep one
      // queryClient.invalidateQueries({ queryKey: ["singlePost", postId] });
    },
  });
};

interface PostRepliesCountResponse {
  count: number;
}

export const useGetPostRepliesCount = (
  postId: string | undefined
): UseQueryResult<number, Error> => {
  return useQuery<PostRepliesCountResponse, Error, number>({
    queryKey: ["postRepliesCount", postId],
    queryFn: async () => {
      if (!postId) throw new Error("Post ID is required to get replies count.");
      await new Promise((resolve) => setTimeout(resolve, 300));
      return { count: Math.floor(Math.random() * 100) };
    },
    select: (data) => data.count,
    enabled: !!postId,
  });
};

export const useGetCommentsForPost = (
  postId: string | undefined
): UseQueryResult<CommentWithFullAuthor[], Error> => {
  return useQuery<
    GetCommentsResponse,
    Error,
    CommentWithFullAuthor[],
    (string | undefined)[]
  >({
    queryKey: ["postComments", postId],
    queryFn: async () => {
      if (!postId) throw new Error("Post ID is required to get comments.");
      const response = await Request.get<GetCommentsResponse>(
        `/forum/replies/post/${postId}`
      );
      return response.data;
    },
    select: (apiResponse: GetCommentsResponse) => {
      if (!apiResponse) {
        throw new Error("Received no response from the comments API.");
      }

      if (apiResponse.statusCode === 200 && apiResponse.data) {
        if (Array.isArray(apiResponse.data)) {
          return apiResponse.data;
        } else {
          throw new Error(
            "Comments data from API is not in the expected array format."
          );
        }
      } else {
        const backendMessage = apiResponse.message;
        const errorMessage =
          backendMessage && backendMessage.toLowerCase() !== "success"
            ? `API Error: ${backendMessage}`
            : "Failed to fetch comments. Please try again.";
        throw new Error(errorMessage);
      }
    },
    enabled: !!postId,
  });
};
type ToggleVars = {
  postId: string;
  replyId: string;
  currentDoctorId: string;
  isCurrentlyLiked: boolean;
};

type ToggleResponse = {
  statusCode: number;
  message: string;
  data?: { upvotedBy?: string[] };
};

// Adjust URL to match your backend if different
export const useToggleReplyLike = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ replyId }: ToggleVars) => {
      const res = await Request.post<ToggleResponse>(
        `/forum/replies/${replyId}/upvote`
      );
      return res.data;
    },
    // optimistic update
    onMutate: async ({
      postId,
      replyId,
      currentDoctorId,
      isCurrentlyLiked,
    }) => {
      await qc.cancelQueries({ queryKey: ["postComments", postId] });

      const previous = qc.getQueryData<CommentWithFullAuthor[]>([
        "postComments",
        postId,
      ]);

      if (previous) {
        const updated = previous.map((r) => {
          if (r._id !== replyId) return r;
          const set = new Set(r.upvotedBy ?? []);
          if (isCurrentlyLiked) set.delete(currentDoctorId);
          else set.add(currentDoctorId);
          return { ...r, upvotedBy: Array.from(set) };
        });
        qc.setQueryData(["postComments", postId], updated);
      }

      return { previous };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(["postComments", vars.postId], ctx.previous);
      }
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: ["postComments", vars.postId] });
    },
  });
};
